<template>

    <v-modal :title="$t('labels.import_customers')">
        <template #alert>
            <v-alert-upgrade feature="customers" />
        </template>
        <template #nav>
            <v-nav>
                <v-nav-item :active="tab === 'add'" :label="$t('labels.manual_entry')" @click.native="setTab('add')" />
                <v-nav-item :active="tab === 'csv'" :label="$t('labels.upload_csv')" @click.native="setTab('csv')" />
                <v-nav-item :active="tab === 'integrations'" :label="$t('labels.integrations')" @click.native="setTab('integrations')" />
                <v-nav-item v-if="project.ingest_email" :active="tab === 'bcc'" :label="$t('labels.bcc')" @click.native="setTab('bcc')" />
                <v-nav-item :active="tab === 'mcp'" :label="$t('labels.mcp')" @click.native="setTab('mcp')" />
            </v-nav>
        </template>
        <template #body>
            <div v-if="tab === 'add'">
                <v-alert :message="error" />
                <v-modal-section :title="$t('titles.customer')" icon="fas fa-user">
                    <v-form-group :label="$t('labels.first_name')" required>
                        <v-input :model="params.first_name" @input="params.first_name = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.last_name')">
                        <v-input :model="params.last_name" @input="params.last_name = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.email_address')">
                        <v-input type="email" :model="params.email" @input="params.email = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.phone_number')">
                        <v-input placeholder="+18888888888" :model="params.phone" @input="params.phone = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.company')">
                        <v-input :model="params.company" @input="params.company = $event" />
                    </v-form-group>
                    <v-form-group v-if="project.locations.length" :label="$t('labels.location')">
                        <v-select-location :model="params.location_id" @input="setLocationId($event)" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.tags')" date="2025-12-29">
                        <v-multiselect-tags context="customer" :model="params.customer_tag_ids" @input="params.customer_tag_ids = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.signed_up')" :caption="$t('captions.signed_up')">
                        <v-date-picker :model="params.signed_up_at" @input="params.signed_up_at = $event" />
                    </v-form-group>
                </v-modal-section>
                <v-modal-section
                    :title="$t('labels.request_review')"
                    icon="fas fa-paper-plane"
                    :inactive="!ask"
                    @input="setAsk($event)"
                >
                    <v-form-group :label="$t('labels.request_date')">
                        <v-select-datetime v-if="ask" :model="params.asked_at" @input="params.asked_at = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.channels')">
                        <v-button-toolbar size="lg">
                            <v-checkbox :label="$t('labels.email')" :model="params.channels.includes('email')" @input="setChannel('email', $event)" :disabled="!project.strategy.channels.includes('email')" />
                            <v-checkbox :label="$t('labels.sms')" :model="params.channels.includes('sms')" @input="setChannel('sms', $event)" :disabled="!project.strategy.channels.includes('sms')" />
                        </v-button-toolbar>
                    </v-form-group>
                    <v-form-group :label="$t('labels.reminders')" :locked="!project.features.reminders">
                        <v-select :model="params.reminders_count" @input="params.reminders_count = $event">
                            <option value="0">{{ $t('labels.no_reminders') }}</option>
                            <option value="1">{{ $t('labels.send_1_reminder') }}</option>
                            <option value="2">{{ $t('labels.send_2_reminders') }}</option>
                            <option value="3">{{ $t('labels.send_3_reminders') }}</option>
                        </v-select>
                    </v-form-group>
                </v-modal-section>
                <v-modal-section v-if="ask" :title="$t('titles.request_options')" collapsed>
                    <v-form-group v-if="project.locations.length" :label="$t('labels.location')" :caption="$t('captions.ask_location')">
                        <v-select-location :model="params.location_id" @input="setLocationId($event)" />
                    </v-form-group>
                    <v-form-group v-if="project.forms.length > 1" :label="$t('labels.review_page')" :caption="$t('captions.ask_review_page')">
                        <v-select-form :model="params.form_id" @input="params.form_id = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.tags')" :caption="$t('captions.assign_tags')">
                        <v-multiselect-tags context="review" :model="params.tag_ids" @input="params.tag_ids = $event" />
                    </v-form-group>
                </v-modal-section>
            </div>
            <div v-else-if="tab === 'csv'">
                <v-modal-section :title="$t('titles.select_csv_file')" icon="fas fa-file-csv">
                    <template #body>
                        <v-alert :message="error" />
                        <v-form-group :caption="$t('captions.csv_max_rows', { count: '1,000' })">
                            <v-uploader-file @input="csvParams.file = $event" />
                        </v-form-group>
                    </template>
                    <template #footer>
                        <v-button size="sm" theme="white" icon="fas fa-file" :loading="downloading" @click.native="downloadTemplate">{{ $t('labels.download_template') }}</v-button>
                        <v-button size="sm" :loading="loading" @click.native="createCustomersWithCSV">{{ $t('labels.upload_csv') }}</v-button>
                    </template>
                </v-modal-section>
                <v-modal-section :title="$t('titles.fields')" icon="fas fa-line-columns" collapsed>
                    <template #body>
                        <v-table bordered vlines>
                            <thead>
                                <th>{{ $t('labels.header') }}</th>
                                <th>{{ $t('labels.value') }}</th>
                            </thead>
                            <tbody>
                                <tr><td>first_name*</td><td>{{ $t('labels.import_first_name') }}</td></tr>
                                <tr><td>last_name</td><td>{{ $t('labels.import_last_name') }}</td></tr>
                                <tr><td>email</td><td>{{ $t('labels.import_email') }}</td></tr>
                                <tr><td>phone</td><td>{{ $t('labels.import_phone') }}</td></tr>
                                <tr><td>company</td><td>{{ $t('labels.import_company') }}</td></tr>
                                <tr><td>location</td><td>{{ $t('labels.import_slug') }} <router-link :to="{ name: 'settings.locations' }">{{ $t('labels.location') }}</router-link>.</td></tr>
                                <tr><td>notes</td><td>{{ $t('labels.import_notes') }}</td></tr>
                                <tr><td>tags</td><td>{{ $t('labels.import_list') }} <router-link :to="{ name: 'settings.tags' }">{{ $t('labels.tags') }}</router-link>.</td></tr>
                                <tr><td>signed_up_at</td><td>{{ $t('labels.import_signed_up_at') }} YYYY-MM-DD</td></tr>
                                <tr><td>address1</td><td>{{ $t('labels.import_address1') }}</td></tr>
                                <tr><td>address2</td><td>{{ $t('labels.import_address2') }}</td></tr>
                                <tr><td>city</td><td>{{ $t('labels.import_city') }}</td></tr>
                                <tr><td>state</td><td>{{ $t('labels.import_state') }}</td></tr>
                                <tr><td>postal_code</td><td>{{ $t('labels.import_postal_code') }}</td></tr>
                            </tbody>
                        </v-table>
                    </template>
                </v-modal-section>
            </div>
            <div v-else-if="tab === 'integrations'">
                <v-modal-section :title="$t('titles.crms')" icon="fas fa-database">
                    <div class="d-grid gap-15">
                        <v-button icon="fab fa-stripe-s" icon-color="#635BFF" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations.show', params: { slug: 'stripe' } })">{{ $t('labels.import_from_stripe') }}</v-button>
                        <v-button icon="fab fa-hubspot" icon-color="#FF7A59" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations.show', params: { slug: 'hubspot' } })">{{ $t('labels.import_from_hubspot') }}</v-button>
                    </div>
                </v-modal-section>
                <v-modal-section :title="$t('titles.application_connectors')" icon="fas fa-plug">
                    <div class="d-grid gap-15">
                        <v-button icon="fas fa-plug" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations', hash: '#application-connectors' })">{{ $t('labels.import_from_zapier') }}</v-button>
                        <v-button icon="fas fa-plug" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations', hash: '#application-connectors' })">{{ $t('labels.import_from_pabbly') }}</v-button>
                        <v-button icon="fas fa-plug" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations', hash: '#application-connectors' })">{{ $t('labels.import_from_make_com') }}</v-button>
                        <v-button icon="fas fa-plug" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations', hash: '#application-connectors' })">{{ $t('labels.import_from_boost_space') }}</v-button>
                    </div>
                </v-modal-section>
            </div>
            <div v-else-if="tab === 'bcc' && project.ingest_email">
                <v-alert type="info" :message="$t('alerts.customer_ingest')" />
                <v-modal-section :title="$t('labels.bcc_email_address')" icon="fas fa-at">
                    <v-copy-box>{{ project.ingest_email }}</v-copy-box>
                </v-modal-section>
            </div>
            <div v-else-if="tab === 'mcp'">
                <v-modal-section-mcp context="customer" />
            </div>
        </template>
        <template #footer>
            <v-button size="sm" theme="white" @click.native="cancel">{{ $t('labels.cancel') }}</v-button>
            <v-button v-if="tab === 'add'" size="sm" :loading="loading" @click.native="createCustomer">{{ $t('labels.save') }}</v-button>
            <div v-else></div>
        </template>
    </v-modal>

</template>

<script>

import ProjectApi from '@api/ProjectApi';
import CopyBox from '@cmp/CopyBox';
import Modal from '@cmp/Modal';
import ModalSection from '@cmp/ModalSection';
import ModalSectionMcp from '@cmp/ModalSectionMcp';
import Nav from '@cmp/Nav';
import NavItem from '@cmp/NavItem';
import DatePicker from '@forms/DatePicker';
import MultiselectTags from '@forms/MultiselectTags';
import SelectDateTime from '@forms/SelectDateTime';
import SelectForm from '@forms/SelectForm';
import SelectLocation from '@forms/SelectLocation';
import UploaderFile from '@forms/UploaderFile';
import AskFormSyncMixin from '@mixins/AskFormSyncMixin';
import moment from 'moment';

export default {

    components: {
        'v-copy-box': CopyBox,
        'v-date-picker': DatePicker,
        'v-modal': Modal,
        'v-modal-section': ModalSection,
        'v-modal-section-mcp': ModalSectionMcp,
        'v-multiselect-tags': MultiselectTags,
        'v-nav': Nav,
        'v-nav-item': NavItem,
        'v-select-datetime': SelectDateTime,
        'v-select-form': SelectForm,
        'v-select-location': SelectLocation,
        'v-uploader-file': UploaderFile,
    },

    mixins: [
        AskFormSyncMixin,
    ],

    data() {
        return {
            loading: false,
            downloading: false,
            error: null,
            tab: 'add',
            ask: false,
            csvParams: {},
            params: {},
        }
    },

    computed: {

        modalTab() {
            return this.$store.getters.modalTab;
        },

    },

    created() {
        this.preselectTab();
        this.params = {
            customer_tag_ids: [],
            signed_up_at: moment().unix(),
            ask: false,
            asked_at: null,
            channels: [...this.project.strategy.channels],
            reminders_count: this.project.strategy.reminders_count,
            form_id: null,
            location_id: this.defaultLocationId(),
            tag_ids: [],
        };
    },

    methods: {

        defaultLocationId() {
            if (this.$store.getters.locationId) {
                return this.$store.getters.locationId;
            }
            if (this.project.locations.length === 1) {
                return this.project.locations[0].id;
            }
            return null;
        },

        preselectTab() {
            if (this.modalTab) {
                let allowedTabs = ['add', 'bcc', 'csv', 'integrations', 'mcp'];
                if (allowedTabs.includes(this.modalTab)) {
                    this.tab = this.modalTab;
                }
                this.$store.commit('setModalTab', null);
            }
        },

        createCustomer() {
            this.loading = true;
            ProjectApi.createCustomer(this.project.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$store.commit('setModal', null);
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                    const modals = document.getElementsByClassName('modal');
                    [].forEach.call(modals, function(el) {
                        el.scrollTo(0, 0);
                    });
                });
        },

        createCustomersWithCSV() {
            this.loading = true;
            let csvParams = new FormData();
            csvParams.append('file', this.csvParams.file);
            ProjectApi.createCustomersWithCSV(this.project.id, csvParams)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('startOperation', {
                        type: 'customer_import',
                        projectId: this.project.id,
                    });
                    this.$store.commit('setModal', null);
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                });
        },

        setTab(val) {
            this.tab = val;
            this.error = null;
        },

        setAsk(val) {
            this.ask = val;
            this.params.ask = val;
            this.params.asked_at = val ? moment().unix() : null;
            if (val) {
                this.syncFormForLocation(this.params.location_id);
            }
        },

        setChannel(channel, value) {
            let index = this.params.channels.indexOf(channel);
            if (value && index < 0) {
                this.params.channels.push(channel)
            } else if (!value && index >= 0) {
                this.params.channels.splice(index, 1);
            }
        },

        downloadTemplate() {
            this.downloading = true;
            ProjectApi.downloadCustomerTemplate(this.project.id)
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'customers.csv';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                })
                .finally(() => {
                    this.downloading = false;
                });
        },

        cancel() {
            this.$store.commit('setModal', null);
        },

    },

}

</script>
